import {
  AccountAnnualIncomeDto,
  AccountBeneficiaryDto,
  AccountFinancialAssetsDto,
  AccountFinancialInvestmentExperienceTypeDto,
  AccountInvestmentExperienceDto,
  AccountInvestmentObjectivesDto,
  AccountLiquidityNeedsDto,
  AccountLiquidNetWorthDto,
  AccountRiskToleranceDto,
  AccountSuitabilityInformationDto,
  AccountTaxBracketDto,
  AccountTimeHorizonDto,
  AccountTotalNetWorthDto,
  AccountTradesPerYearDto,
  AccountTypeDto,
  AccountYearsOfExperienceDto,
  EntityAccountTypeDto,
  EntityInformationDto,
  JointAccountTypeDto,
  RetirementAccountTypeDto,
} from 'src/dtos';
import { Maybe } from 'src/utils';

import { AccountHolder, AccountHolderAddress } from './accountHolders.models';
import { InvestigationStatusValue } from './investigations.models';

export enum AccountTypeLabel {
  Individual = 'Individual',
  Retirement = 'Retirement',
  Joint = 'Joint',
  Ugma = 'UGMA',
  Entity = 'Entity',
}

export class AccountType {
  private _value: AccountTypeDto;
  private _label: AccountTypeLabel;

  constructor(type: AccountTypeDto) {
    this._value = type;
    this._label = this.findLabel();
  }

  private findLabel(): AccountTypeLabel {
    switch (this._value) {
      case AccountTypeDto.Individual:
        return AccountTypeLabel.Individual;
      case AccountTypeDto.Joint:
        return AccountTypeLabel.Joint;
      case AccountTypeDto.Retirement:
        return AccountTypeLabel.Retirement;
      case AccountTypeDto.Ugma:
        return AccountTypeLabel.Ugma;
      case AccountTypeDto.Entity:
        return AccountTypeLabel.Entity;
    }
  }

  get value(): AccountTypeDto {
    return this._value;
  }

  get label(): AccountTypeLabel {
    return this._label;
  }

  get isIndividual(): boolean {
    return this._value === AccountTypeDto.Individual;
  }

  get isJoint(): boolean {
    return this._value === AccountTypeDto.Joint;
  }

  get isRetirement(): boolean {
    return this._value === AccountTypeDto.Retirement;
  }

  get isUgma(): boolean {
    return this._value === AccountTypeDto.Ugma;
  }

  get isEntity(): boolean {
    return this._value === AccountTypeDto.Entity;
  }
}

export enum JointAccountTypeLabel {
  Community = 'Community Property',
  Survivor = 'Rights of Survivorship',
  TenantsInCommon = 'Tenants in Common',
  TenantsInEntirety = 'Tenants in Entirety',
}

export class JointAccountType {
  private _value?: JointAccountTypeDto;
  private _label?: JointAccountTypeLabel;

  constructor(type?: JointAccountTypeDto) {
    this._value = type;
    this._label = this.findLabel();
  }

  private findLabel(): Maybe<JointAccountTypeLabel> {
    switch (this._value) {
      case JointAccountTypeDto.Community:
        return JointAccountTypeLabel.Community;
      case JointAccountTypeDto.Survivor:
        return JointAccountTypeLabel.Survivor;
      case JointAccountTypeDto.TenantsInCommon:
        return JointAccountTypeLabel.TenantsInCommon;
      case JointAccountTypeDto.TenantsInEntirety:
        return JointAccountTypeLabel.TenantsInEntirety;
    }
  }

  get value(): Maybe<JointAccountTypeDto> {
    return this._value;
  }

  get label(): Maybe<JointAccountTypeLabel> {
    return this._label;
  }

  get isCommunity(): boolean {
    return this._value === JointAccountTypeDto.Community;
  }

  get isSurvivor(): boolean {
    return this._value === JointAccountTypeDto.Survivor;
  }

  get isTenantsInCommon(): boolean {
    return this._value === JointAccountTypeDto.TenantsInCommon;
  }

  get isTenantsInEntirety(): boolean {
    return this._value === JointAccountTypeDto.TenantsInEntirety;
  }
}

export enum RetirementAccountTypeLabel {
  Traditional = 'Traditional IRA',
  Rollover = 'IRA Rollover',
  Roth = 'Roth IRA',
  SEP = 'SEP IRA',
  Simple = 'Simple IRA',
  Beneficiary = 'Beneficiary IRA',
  CoverDell = 'Coverdell IRA',
  RothBeneficiary = 'Roth Beneficiary IRA',
}

export class RetirementAccountType {
  private _value?: RetirementAccountTypeDto;
  private _label?: RetirementAccountTypeLabel;

  constructor(type?: RetirementAccountTypeDto) {
    this._value = type;
    this._label = this.findLabel();
  }

  private findLabel(): Maybe<RetirementAccountTypeLabel> {
    switch (this._value) {
      case RetirementAccountTypeDto.Traditional:
        return RetirementAccountTypeLabel.Traditional;
      case RetirementAccountTypeDto.Simple:
        return RetirementAccountTypeLabel.Simple;
      case RetirementAccountTypeDto.SEP:
        return RetirementAccountTypeLabel.SEP;
      case RetirementAccountTypeDto.CoverDell:
        return RetirementAccountTypeLabel.CoverDell;
      case RetirementAccountTypeDto.Roth:
        return RetirementAccountTypeLabel.Roth;
      case RetirementAccountTypeDto.Rollover:
        return RetirementAccountTypeLabel.Rollover;
      case RetirementAccountTypeDto.RothBeneficiary:
        return RetirementAccountTypeLabel.RothBeneficiary;
      case RetirementAccountTypeDto.Beneficiary:
        return RetirementAccountTypeLabel.Beneficiary;
    }
  }

  get value(): Maybe<RetirementAccountTypeDto> {
    return this._value;
  }

  get label(): Maybe<RetirementAccountTypeLabel> {
    return this._label;
  }

  get isSimple(): boolean {
    return this._value === RetirementAccountTypeDto.Simple;
  }

  get isBeneficiary(): boolean {
    return this._value === RetirementAccountTypeDto.Beneficiary;
  }

  get isRoth(): boolean {
    return this._value === RetirementAccountTypeDto.Roth;
  }

  get isRothBeneficiary(): boolean {
    return this._value === RetirementAccountTypeDto.RothBeneficiary;
  }

  get isRollover(): boolean {
    return this._value === RetirementAccountTypeDto.Rollover;
  }
}

export enum EntityAccountTypeLabel {
  CCorporation = 'C-Corporation (C-Corp)',
  SCorporation = 'S-Corporation (S-Corp)',
  LlcCCorporation = 'LLC (C-Corp-Tax Class)',
  LlcSCorporation = 'LLC (S-Corp-Tax Class)',
  LlcPartnership = 'LLC (Partnership-Tax Class)',
  PersonalTrust = 'Personal Trust',
  BusinessTrust = 'Business Trust',
  Partnership = 'Partnership',
}

export class EntityAccountType {
  private _value?: EntityAccountTypeDto;
  private _label?: EntityAccountTypeLabel;

  constructor(type?: EntityAccountTypeDto) {
    this._value = type;
    this._label = this.findLabel();
  }

  private findLabel(): Maybe<EntityAccountTypeLabel> {
    switch (this._value) {
      case EntityAccountTypeDto.CCorporation:
        return EntityAccountTypeLabel.CCorporation;
      case EntityAccountTypeDto.SCorporation:
        return EntityAccountTypeLabel.SCorporation;
      case EntityAccountTypeDto.LlcCCorporation:
        return EntityAccountTypeLabel.LlcCCorporation;
      case EntityAccountTypeDto.LlcSCorporation:
        return EntityAccountTypeLabel.LlcSCorporation;
      case EntityAccountTypeDto.LlcPartnership:
        return EntityAccountTypeLabel.LlcPartnership;
      case EntityAccountTypeDto.PersonalTrust:
        return EntityAccountTypeLabel.PersonalTrust;
      case EntityAccountTypeDto.BusinessTrust:
        return EntityAccountTypeLabel.BusinessTrust;
      case EntityAccountTypeDto.Partnership:
        return EntityAccountTypeLabel.Partnership;
    }
  }

  get value(): Maybe<EntityAccountTypeDto> {
    return this._value;
  }

  get label(): Maybe<EntityAccountTypeLabel> {
    return this._label;
  }

  get isSCorporate(): boolean {
    return this._value === EntityAccountTypeDto.SCorporation;
  }

  get isCCorporate(): boolean {
    return this._value === EntityAccountTypeDto.CCorporation;
  }

  get isCorporate(): boolean {
    return this.isSCorporate || this.isCCorporate;
  }

  get isLlcCCorporate(): boolean {
    return this._value === EntityAccountTypeDto.LlcCCorporation;
  }

  get isLlcSCorporate(): boolean {
    return this._value === EntityAccountTypeDto.LlcSCorporation;
  }

  get isLlcPartnership(): boolean {
    return this._value === EntityAccountTypeDto.LlcPartnership;
  }

  get isLlc(): boolean {
    return this.isLlcCCorporate || this.isLlcSCorporate || this.isLlcPartnership;
  }

  get isPartnership(): boolean {
    return this._value === EntityAccountTypeDto.Partnership;
  }

  get isPersonalTrust(): boolean {
    return this._value === EntityAccountTypeDto.PersonalTrust;
  }

  get isBusinessTrust(): boolean {
    return this._value === EntityAccountTypeDto.BusinessTrust;
  }

  get isTrust(): boolean {
    return this.isPersonalTrust || this.isBusinessTrust;
  }
}

export interface EntityInformation extends Omit<EntityInformationDto, 'address'> {
  address: AccountHolderAddress;
}

export enum AccountLiquidityNeedsLabel {
  NotImportant = 'Not Important',
  SomewhatImportant = 'Somewhat Important',
  VeryImportant = 'Very Important',
}

export enum AccountInvestmentObjectivesLabel {
  CapitalPreservation = 'Capital Preservation',
  Income = 'Income',
  Growth = 'Growth',
  GrowthAndIncome = 'Growth and Income',
  Speculation = 'Speculation',
}

export enum AccountRiskToleranceLabel {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export enum AccountTimeHorizonLabel {
  Short = 'Short',
  Average = 'Average',
  Long = 'Long',
}

export class AccountSuitabilityInformation {
  private _liquidityNeeds: {
    value: AccountLiquidityNeedsDto;
    label: AccountLiquidityNeedsLabel;
  };
  private _investmentObjectives: {
    value: AccountInvestmentObjectivesDto;
    label: AccountInvestmentObjectivesLabel;
  };
  private _riskTolerance: { value: AccountRiskToleranceDto; label: AccountRiskToleranceLabel };
  private _timeHorizon: { value: AccountTimeHorizonDto; label: AccountTimeHorizonLabel };
  private _financialInvestmentExperience: Maybe<AccountFinancialInvestmentExperience>;

  constructor(suitabilityInformation: AccountSuitabilityInformationDto) {
    this._liquidityNeeds = {
      value: suitabilityInformation.liquidityNeeds,
      label: this.findLiquidityNeedsLabel(suitabilityInformation.liquidityNeeds),
    };
    this._investmentObjectives = {
      value: suitabilityInformation.investmentObjectives,
      label: this.findInvestmentObjectivesLabel(suitabilityInformation.investmentObjectives),
    };
    this._riskTolerance = {
      value: suitabilityInformation.riskTolerance,
      label: this.findRiskToleranceLabel(suitabilityInformation.riskTolerance),
    };
    this._timeHorizon = {
      value: suitabilityInformation.timeHorizon,
      label: this.findTimeHorizonLabel(suitabilityInformation.timeHorizon),
    };
    this._financialInvestmentExperience = suitabilityInformation.financialInvestmentExperience
      ? new AccountFinancialInvestmentExperience(suitabilityInformation.financialInvestmentExperience)
      : undefined;
  }

  private findLiquidityNeedsLabel(value: AccountLiquidityNeedsDto): AccountLiquidityNeedsLabel {
    switch (value) {
      case AccountLiquidityNeedsDto.NotImportant:
        return AccountLiquidityNeedsLabel.NotImportant;
      case AccountLiquidityNeedsDto.SomewhatImportant:
        return AccountLiquidityNeedsLabel.SomewhatImportant;
      case AccountLiquidityNeedsDto.VeryImportant:
        return AccountLiquidityNeedsLabel.VeryImportant;
    }
  }

  private findInvestmentObjectivesLabel(value: AccountInvestmentObjectivesDto): AccountInvestmentObjectivesLabel {
    switch (value) {
      case AccountInvestmentObjectivesDto.CapitalPreservation:
        return AccountInvestmentObjectivesLabel.CapitalPreservation;
      case AccountInvestmentObjectivesDto.Income:
        return AccountInvestmentObjectivesLabel.Income;
      case AccountInvestmentObjectivesDto.Growth:
        return AccountInvestmentObjectivesLabel.Growth;
      case AccountInvestmentObjectivesDto.GrowthAndIncome:
        return AccountInvestmentObjectivesLabel.GrowthAndIncome;
      case AccountInvestmentObjectivesDto.Speculation:
        return AccountInvestmentObjectivesLabel.Speculation;
    }
  }

  private findRiskToleranceLabel(value: AccountRiskToleranceDto): AccountRiskToleranceLabel {
    switch (value) {
      case AccountRiskToleranceDto.Low:
        return AccountRiskToleranceLabel.Low;
      case AccountRiskToleranceDto.Medium:
        return AccountRiskToleranceLabel.Medium;
      case AccountRiskToleranceDto.High:
        return AccountRiskToleranceLabel.High;
    }
  }

  private findTimeHorizonLabel(value: AccountTimeHorizonDto): AccountTimeHorizonLabel {
    switch (value) {
      case AccountTimeHorizonDto.Short:
        return AccountTimeHorizonLabel.Short;
      case AccountTimeHorizonDto.Average:
        return AccountTimeHorizonLabel.Average;
      case AccountTimeHorizonDto.Long:
        return AccountTimeHorizonLabel.Long;
    }
  }

  get liquidityNeeds(): { value: AccountLiquidityNeedsDto; label: AccountLiquidityNeedsLabel } {
    return this._liquidityNeeds;
  }

  get investmentObjectives(): {
    value: AccountInvestmentObjectivesDto;
    label: AccountInvestmentObjectivesLabel;
  } {
    return this._investmentObjectives;
  }

  get riskTolerance(): { value: AccountRiskToleranceDto; label: AccountRiskToleranceLabel } {
    return this._riskTolerance;
  }

  get timeHorizon(): { value: AccountTimeHorizonDto; label: AccountTimeHorizonLabel } {
    return this._timeHorizon;
  }

  get financialInvestmentExperience(): Maybe<AccountFinancialInvestmentExperience> {
    return this._financialInvestmentExperience;
  }
}

export enum AccountYearsOfExperienceLabel {
  One = '1',
  TwoToThree = '2-3',
  FourToNine = '4-9',
  TenOrMore = '10+',
}

export enum AccountTradesPerYearLabel {
  OneToFive = '1-5',
  SixToTen = '6-10',
  ElevenToTwenty = '11-20',
  MoreThanTwenty = '20+',
}

export class AccountFinancialInvestmentExperienceType {
  private _tradesPerYear: { value: AccountTradesPerYearDto; label: AccountTradesPerYearLabel };
  private _yearsOfExperience: {
    value: AccountYearsOfExperienceDto;
    label: AccountYearsOfExperienceLabel;
  };

  constructor(dto: AccountFinancialInvestmentExperienceTypeDto) {
    this._tradesPerYear = {
      value: dto.tradesPerYear,
      label: this.findTradesPerYearLabel(dto.tradesPerYear),
    };
    this._yearsOfExperience = {
      value: dto.yearsOfExperience,
      label: this.findYearsOfExperienceLabel(dto.yearsOfExperience),
    };
  }

  private findYearsOfExperienceLabel(value: AccountYearsOfExperienceDto): AccountYearsOfExperienceLabel {
    switch (value) {
      case AccountYearsOfExperienceDto.One:
        return AccountYearsOfExperienceLabel.One;
      case AccountYearsOfExperienceDto.TwoToThree:
        return AccountYearsOfExperienceLabel.TwoToThree;
      case AccountYearsOfExperienceDto.FourToNine:
        return AccountYearsOfExperienceLabel.FourToNine;
      case AccountYearsOfExperienceDto.TenOrMore:
        return AccountYearsOfExperienceLabel.TenOrMore;
    }
  }

  private findTradesPerYearLabel(value: AccountTradesPerYearDto): AccountTradesPerYearLabel {
    switch (value) {
      case AccountTradesPerYearDto.OneToFive:
        return AccountTradesPerYearLabel.OneToFive;
      case AccountTradesPerYearDto.SixToTen:
        return AccountTradesPerYearLabel.SixToTen;
      case AccountTradesPerYearDto.ElevenToTwenty:
        return AccountTradesPerYearLabel.ElevenToTwenty;
      case AccountTradesPerYearDto.MoreThanTwenty:
        return AccountTradesPerYearLabel.MoreThanTwenty;
    }
  }

  get tradesPerYear(): { value: AccountTradesPerYearDto; label: AccountTradesPerYearLabel } {
    return this._tradesPerYear;
  }

  get yearsOfExperience(): { value: AccountYearsOfExperienceDto; label: AccountYearsOfExperienceLabel } {
    return this._yearsOfExperience;
  }
}

export class AccountFinancialInvestmentExperience {
  private _stocks: Maybe<AccountFinancialInvestmentExperienceType>;
  private _options: Maybe<AccountFinancialInvestmentExperienceType>;
  private _commodities: Maybe<AccountFinancialInvestmentExperienceType>;
  private _bonds: Maybe<AccountFinancialInvestmentExperienceType>;
  private _margins: Maybe<AccountFinancialInvestmentExperienceType>;

  constructor({ stocks, options, commodities, bonds, margins }: AccountInvestmentExperienceDto) {
    this._stocks = stocks ? new AccountFinancialInvestmentExperienceType(stocks) : undefined;
    this._options = options ? new AccountFinancialInvestmentExperienceType(options) : undefined;
    this._commodities = commodities ? new AccountFinancialInvestmentExperienceType(commodities) : undefined;
    this._bonds = bonds ? new AccountFinancialInvestmentExperienceType(bonds) : undefined;
    this._margins = margins ? new AccountFinancialInvestmentExperienceType(margins) : undefined;
  }

  get stocks(): Maybe<AccountFinancialInvestmentExperienceType> {
    return this._stocks;
  }

  get options(): Maybe<AccountFinancialInvestmentExperienceType> {
    return this._options;
  }

  get commodities(): Maybe<AccountFinancialInvestmentExperienceType> {
    return this._commodities;
  }

  get bonds(): Maybe<AccountFinancialInvestmentExperienceType> {
    return this._bonds;
  }

  get margins(): Maybe<AccountFinancialInvestmentExperienceType> {
    return this._margins;
  }
}

export enum AccountAnnualIncomeLabel {
  LessThan25K = '$0K-$25K',
  Between25KAnd50K = '$25K-$50K',
  Between50KAnd100K = '$50K-$100K',
  Between100KAnd200K = '$100K-$200K',
  Between200KAnd300K = '$200K-$300K',
  Between300KAnd500K = '$300K-$500K',
  Between500KAnd1Point2M = '$500K-$1.2M',
  MoreThan1Point2M = '$1.2M+',
}

export enum AccountTotalNetWorthLabel {
  LessThan50K = '$0K-$50K',
  Between50KAnd100K = '$50K-$100K',
  Between100KAnd200K = '$100K-$200K',
  Between200KAnd500K = '$200K-$500K',
  Between500KAnd1M = '$500K-$1M',
  Between1MAnd5M = '$1M-$5M',
  MoreThan5M = '$5M+',
}

export enum AccountLiquidNetWorthLabel {
  LessThan50K = '$0K-$50K',
  Between50KAnd100K = '$50K-$100K',
  Between100KAnd200K = '$100K-$200K',
  Between200KAnd500K = '$200K-$500K',
  Between500KAnd1M = '$500K-$1M',
  Between1MAnd5M = '$1M-$5M',
  MoreThan5M = '$5M+',
}

export enum AccountTaxBracketLabel {
  Bottom = '10-19%',
  Middle = '20-29%',
  Top = '30-37%',
}

export enum AccountEmploymentStatusLabel {
  Employed = 'Employed',
  Retired = 'Retired',
  Student = 'Student',
  Unemployed = 'Unemployed',
}

export enum AccountYearsEmployedLabel {
  BetweenZeroAndOne = 'Less than 1 year',
  BetweenTwoAndFive = '2 to 5 years',
  BetweenFiveAndTen = '5 to 10 years',
  BetweenTenAndTwenty = '10 to 20 years',
  MoreThanTwenty = 'More than 20 years',
}

interface Range {
  min: number;
  max: number;
}

export class AccountFinancialAssets {
  private _annualIncomeValue: Maybe<AccountAnnualIncomeDto>;
  private _annualIncomeLabel: Maybe<AccountAnnualIncomeLabel>;
  private _totalNetWorthValue: Maybe<AccountTotalNetWorthDto>;
  private _totalNetWorthLabel: Maybe<AccountTotalNetWorthLabel>;
  private _liquidNetWorthValue: Maybe<AccountLiquidNetWorthDto>;
  private _liquidNetWorthLabel: Maybe<AccountLiquidNetWorthLabel>;
  private _taxBracketValue: Maybe<AccountTaxBracketDto>;
  private _taxBracketLabel: Maybe<AccountTaxBracketLabel>;
  private _annualIncomeBucketToRangeMapper: Record<AccountAnnualIncomeDto, Range> = {
    lessThan25K: { min: 0, max: 24999 },
    between25KAnd50K: { min: 25000, max: 49999 },
    between50KAnd100K: { min: 50000, max: 99999 },
    between100KAnd200K: { min: 100000, max: 199999 },
    between200KAnd300K: { min: 200000, max: 299999 },
    between300KAnd500K: { min: 300000, max: 499999 },
    between500KAnd1Point2M: { min: 500000, max: 1199999 },
    moreThan1Point2M: { min: 1200000, max: Number.MAX_VALUE },
  };
  private _totalNetWorthBucketToRangeMapper: Record<AccountTotalNetWorthDto, Range> = {
    lessThan50K: { min: 0, max: 49999 },
    between50KAnd100K: { min: 50000, max: 99999 },
    between100KAnd200K: { min: 100000, max: 199999 },
    between200KAnd500K: { min: 200000, max: 499999 },
    between500KAnd1M: { min: 50000, max: 999999 },
    between1MAnd5M: { min: 1000000, max: 4999999 },
    moreThan5M: { min: 5000000, max: Number.MAX_VALUE },
  };
  private _liquidNetWorthBucketToRangeMapper: Record<AccountLiquidNetWorthDto, Range> = {
    lessThan50K: { min: 0, max: 49999 },
    between50KAnd100K: { min: 50000, max: 99999 },
    between100KAnd200K: { min: 100000, max: 199999 },
    between200KAnd500K: { min: 200000, max: 499999 },
    between500KAnd1M: { min: 500000, max: 999999 },
    between1MAnd5M: { min: 1000000, max: 4999999 },
    moreThan5M: { min: 5000000, max: Number.MAX_VALUE },
  };

  constructor(dto: AccountFinancialAssetsDto) {
    this._annualIncomeValue = dto.annualIncome;
    this._annualIncomeLabel = dto.annualIncome ? this.findAnnualIncomeLabel(dto.annualIncome) : undefined;
    this._totalNetWorthValue = dto.totalNetWorth;
    this._totalNetWorthLabel = dto.totalNetWorth ? this.findTotalNetWorthLabel(dto.totalNetWorth) : undefined;
    this._liquidNetWorthValue = dto.liquidNetWorth;
    this._liquidNetWorthLabel = dto.liquidNetWorth ? this.findLiquidNetWorthLabel(dto.liquidNetWorth) : undefined;
    this._taxBracketValue = dto.taxBracket;
    this._taxBracketLabel = dto.taxBracket ? this.findTaxBracketLabel(dto.taxBracket) : undefined;
  }

  private findAnnualIncomeLabel(annualIncome: AccountAnnualIncomeDto): AccountAnnualIncomeLabel {
    switch (annualIncome) {
      case AccountAnnualIncomeDto.LessThan25K:
        return AccountAnnualIncomeLabel.LessThan25K;
      case AccountAnnualIncomeDto.Between25KAnd50K:
        return AccountAnnualIncomeLabel.Between25KAnd50K;
      case AccountAnnualIncomeDto.Between50KAnd100K:
        return AccountAnnualIncomeLabel.Between50KAnd100K;
      case AccountAnnualIncomeDto.Between100KAnd200K:
        return AccountAnnualIncomeLabel.Between100KAnd200K;
      case AccountAnnualIncomeDto.Between200KAnd300K:
        return AccountAnnualIncomeLabel.Between200KAnd300K;
      case AccountAnnualIncomeDto.Between300KAnd500K:
        return AccountAnnualIncomeLabel.Between300KAnd500K;
      case AccountAnnualIncomeDto.Between500KAnd1Point2M:
        return AccountAnnualIncomeLabel.Between500KAnd1Point2M;
      case AccountAnnualIncomeDto.MoreThan1Point2M:
        return AccountAnnualIncomeLabel.MoreThan1Point2M;
    }
  }

  private findTotalNetWorthLabel(totalNetWorth: AccountTotalNetWorthDto): AccountTotalNetWorthLabel {
    switch (totalNetWorth) {
      case AccountTotalNetWorthDto.LessThan50K:
        return AccountTotalNetWorthLabel.LessThan50K;
      case AccountTotalNetWorthDto.Between50KAnd100K:
        return AccountTotalNetWorthLabel.Between50KAnd100K;
      case AccountTotalNetWorthDto.Between100KAnd200K:
        return AccountTotalNetWorthLabel.Between100KAnd200K;
      case AccountTotalNetWorthDto.Between200KAnd500K:
        return AccountTotalNetWorthLabel.Between200KAnd500K;
      case AccountTotalNetWorthDto.Between500KAnd1M:
        return AccountTotalNetWorthLabel.Between500KAnd1M;
      case AccountTotalNetWorthDto.Between1MAnd5M:
        return AccountTotalNetWorthLabel.Between1MAnd5M;
      case AccountTotalNetWorthDto.MoreThan5M:
        return AccountTotalNetWorthLabel.MoreThan5M;
    }
  }

  private findLiquidNetWorthLabel(liquidNetWorth: AccountLiquidNetWorthDto): AccountLiquidNetWorthLabel {
    switch (liquidNetWorth) {
      case AccountLiquidNetWorthDto.LessThan50K:
        return AccountLiquidNetWorthLabel.LessThan50K;
      case AccountLiquidNetWorthDto.Between50KAnd100K:
        return AccountLiquidNetWorthLabel.Between50KAnd100K;
      case AccountLiquidNetWorthDto.Between100KAnd200K:
        return AccountLiquidNetWorthLabel.Between100KAnd200K;
      case AccountLiquidNetWorthDto.Between200KAnd500K:
        return AccountLiquidNetWorthLabel.Between200KAnd500K;
      case AccountLiquidNetWorthDto.Between500KAnd1M:
        return AccountLiquidNetWorthLabel.Between500KAnd1M;
      case AccountLiquidNetWorthDto.Between1MAnd5M:
        return AccountLiquidNetWorthLabel.Between1MAnd5M;
      case AccountLiquidNetWorthDto.MoreThan5M:
        return AccountLiquidNetWorthLabel.MoreThan5M;
    }
  }

  private findTaxBracketLabel(taxBracket: AccountTaxBracketDto): AccountTaxBracketLabel {
    switch (taxBracket) {
      case AccountTaxBracketDto.Bottom:
        return AccountTaxBracketLabel.Bottom;
      case AccountTaxBracketDto.Middle:
        return AccountTaxBracketLabel.Middle;
      case AccountTaxBracketDto.Top:
        return AccountTaxBracketLabel.Top;
    }
  }

  get annualIncome(): Maybe<{
    value: AccountAnnualIncomeDto;
    label: AccountAnnualIncomeLabel;
    range: Range;
  }> {
    return this._annualIncomeValue && this._annualIncomeLabel
      ? {
          value: this._annualIncomeValue,
          label: this._annualIncomeLabel,
          range: this._annualIncomeBucketToRangeMapper[this._annualIncomeValue],
        }
      : undefined;
  }

  get totalNetWorth(): Maybe<{
    value: AccountTotalNetWorthDto;
    label: AccountTotalNetWorthLabel;
    range: Range;
  }> {
    return this._totalNetWorthValue && this._totalNetWorthLabel
      ? {
          value: this._totalNetWorthValue,
          label: this._totalNetWorthLabel,
          range: this._totalNetWorthBucketToRangeMapper[this._totalNetWorthValue],
        }
      : undefined;
  }

  get liquidNetWorth(): Maybe<{
    value: AccountLiquidNetWorthDto;
    label: AccountLiquidNetWorthLabel;
    range: Range;
  }> {
    return this._liquidNetWorthValue && this._liquidNetWorthLabel
      ? {
          value: this._liquidNetWorthValue,
          label: this._liquidNetWorthLabel,
          range: this._liquidNetWorthBucketToRangeMapper[this._liquidNetWorthValue],
        }
      : undefined;
  }

  get taxBracket(): Maybe<{ value: AccountTaxBracketDto; label: AccountTaxBracketLabel }> {
    return this._taxBracketValue && this._taxBracketLabel
      ? { value: this._taxBracketValue, label: this._taxBracketLabel }
      : undefined;
  }
}

export interface Account {
  id: string;
  type: AccountType;
  customTypeLabel: string;
  accountId?: number;
  accountNumber?: string;
  isRoot: boolean;
  isAccredited?: boolean;
  primaryAccountHolder?: AccountHolder;
  secondaryAccountHolder?: AccountHolder;
  jointAccountType?: JointAccountType;
  jointCommunityPropertyState?: { label?: string; value: string };
  retirementAccountType?: RetirementAccountType;
  simpleIraPlanName?: string;
  beneficiaryIraDecedentName?: string;
  beneficiaries: Array<AccountBeneficiaryDto>;
  entityAccountType?: EntityAccountType;
  isEntityAccountTypeActive?: boolean;
  otherAccountHolders?: Array<AccountHolder>;
  entityInformation?: EntityInformation;
  applicationId?: string;
  suitabilityInformation?: AccountSuitabilityInformation;
  financialAssets?: AccountFinancialAssets;
  failedCreateAttempts?: number;
  lastFailedCreateAttemptError?: string;
  lastFailedCreateAttemptAt?: string;
  failedLinkAttempts?: number;
  lastFailedLinkAttemptError?: string;
  lastFailedLinkAttemptAt?: string;
  createdAt: string;
}

export enum AccountStatusLabel {
  Pending = 'PENDING',
  Active = 'ACTIVE',
  Rejected = 'REJECTED',
}

export enum AccountStatusValue {
  Pending = 'Pending',
  Active = 'Active',
  Rejected = 'Rejected',
  PendingLinkError = 'PendingLinkError',
  PendingEntityActivation = 'PendingEntityActivation',
}

export class AccountStatus {
  _value: AccountStatusValue;
  _label: AccountStatusLabel;

  constructor(
    state: InvestigationStatusValue | AccountStatusValue.PendingLinkError | AccountStatusValue.PendingEntityActivation,
  ) {
    this._value = this.findValue(state);
    this._label = this.findLabel();
  }

  private findValue(
    state: InvestigationStatusValue | AccountStatusValue.PendingLinkError | AccountStatusValue.PendingEntityActivation,
  ): AccountStatusValue {
    switch (state) {
      case InvestigationStatusValue.Active:
        return AccountStatusValue.Active;
      case InvestigationStatusValue.Rejected:
        return AccountStatusValue.Rejected;
      case AccountStatusValue.PendingLinkError:
        return AccountStatusValue.PendingLinkError;
      case AccountStatusValue.PendingEntityActivation:
        return AccountStatusValue.PendingEntityActivation;
      default:
        return AccountStatusValue.Pending;
    }
  }

  private findLabel(): AccountStatusLabel {
    switch (this._value) {
      case AccountStatusValue.Active:
        return AccountStatusLabel.Active;
      case AccountStatusValue.Rejected:
        return AccountStatusLabel.Rejected;
      default:
        return AccountStatusLabel.Pending;
    }
  }

  get label(): AccountStatusLabel {
    return this._label;
  }

  get value(): AccountStatusValue {
    return this._value;
  }

  get isActive(): boolean {
    return this._value === AccountStatusValue.Active;
  }

  get isRejected(): boolean {
    return this._value === AccountStatusValue.Rejected;
  }

  get isPending(): boolean {
    return this._value === AccountStatusValue.Pending;
  }

  get isPendingLinkError(): boolean {
    return this._value === AccountStatusValue.PendingLinkError;
  }

  get isPendingEntityActivation(): boolean {
    return this._value === AccountStatusValue.PendingEntityActivation;
  }
}
